import { useContext, useMemo } from 'react'
import { StakeContext } from '../context'

export const CATEGORIES: Array<{ value: Exclude<InventoryCategory, 'all'>; label: string }> = [
    { value: 'ape', label: 'Apes' },
    { value: 'droid', label: 'Droids' },
    { value: 'lootbox', label: 'Loot Boxes' },
    { value: 'weapon', label: 'Weapons' },
]

type CategoryStat = {
    value: Exclude<InventoryCategory, 'all'>
    label: string
    count: number
    active: number
    rate: number
}

/**
 * Everything the inventory page needs to describe what the user owns.
 *
 * Replaces the old `useStakableNFTs`, which exported fourteen separately-memoised slices (two of
 * them keyed on the wrong dependency). One pass over the data produces the same information.
 */
export const useInventory = () => {
    const { data, isFetched, useStake, useUnstake } = useContext(StakeContext)

    const active = useMemo(() => data.filter((nft) => !!nft.staked), [data])
    const inactive = useMemo(() => data.filter((nft) => !nft.staked), [data])

    /** Points per day the user is currently earning across every active item. */
    const rate = useMemo(() => active.reduce((total, nft) => total + nft.stake_rate, 0), [active])

    const categories = useMemo<CategoryStat[]>(
        () =>
            CATEGORIES.map(({ value, label }) => {
                const owned = data.filter((nft) => nft.operative_type === value)
                const activeInCategory = owned.filter((nft) => !!nft.staked)
                return {
                    value,
                    label,
                    count: owned.length,
                    active: activeInCategory.length,
                    rate: activeInCategory.reduce((total, nft) => total + nft.stake_rate, 0),
                }
            }),
        [data]
    )

    return { data, isFetched, useStake, useUnstake, active, inactive, rate, categories }
}
